// src/store/notifications/notificationsActions.js
import { get, put, del } from '../../utils/apiClient'

export const addNotification = (notification) => ({
  type: 'ADD_NOTIFICATION',
  payload: {
    ...notification,
    id: notification.id ?? `notif-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    timestamp: notification.timestamp || new Date().toISOString(),
    read: notification.read || false,
  },
})

export const markNotificationRead = (notificationId) => ({
  type: 'MARK_NOTIFICATION_READ',
  payload: notificationId,
})

export const markAllNotificationsRead = () => ({
  type: 'MARK_ALL_NOTIFICATIONS_READ',
})

export const clearNotification = (notificationId) => ({
  type: 'CLEAR_NOTIFICATION',
  payload: notificationId,
})

export const setNotifications = (notifications) => ({
  type: 'SET_NOTIFICATIONS',
  // S'assurer que notifications est toujours un tableau
  payload: Array.isArray(notifications) ? notifications : [],
})

export const setUnreadCount = (count) => ({
  type: 'SET_UNREAD_COUNT',
  payload: count,
})

export const fetchNotifications = () => async (dispatch) => {
  try {
    // ✅ SECURITY: Vérifier via userId (cookie httpOnly gère l'auth)
    const userId = localStorage.getItem('userId')
    if (!userId) return

    const data = await get('/notifications')

    // Gérer les différents formats de réponse possibles
    const notifications = Array.isArray(data) ? data : data.rows || []

    // Dispatch les notifications sans afficher de toasts
    dispatch(setNotifications(notifications))
  } catch (error) {
    console.error('Erreur lors de la récupération des notifications:', error)
    // En cas d'erreur, définir un tableau vide de notifications
    dispatch(setNotifications([]))
  }
}

// Action pour ajouter une notification de vue de profil
export const addProfileViewNotification = (viewData) => (dispatch) => {
  const notification = createProfileViewNotification(viewData)
  dispatch(addNotification(notification))
}

export const markNotificationReadOnServer = (notificationId) => async (dispatch) => {
  // ✅ SECURITY: Vérifier via userId (cookie httpOnly gère l'auth)
  const userId = localStorage.getItem('userId')
  if (!userId) return

  // Optimiste : on met l'UI à jour TOUT DE SUITE (le badge tombe sans attendre
  // le réseau), puis on persiste en fond. En cas d'échec, on resynchronise.
  dispatch(markNotificationRead(notificationId))
  try {
    await put(`/notifications/${notificationId}/read`, {})
  } catch (error) {
    console.error('Erreur lors du marquage de la notification comme lue:', error)
    dispatch(fetchNotifications()) // rollback : resync depuis le serveur
  }
}

// Identifiant d'une notification PERSISTÉE côté serveur : un entier (clé de la table notifications).
// Les notifications créées côté navigateur (« notif-…», « profile-view-… ») n'existent qu'en mémoire.
const isServerNotificationId = (id) => /^\d+$/.test(String(id))

/**
 * Supprime une notification : retrait immédiat de la liste, PUIS suppression côté serveur.
 * Sans l'appel serveur, la notification revenait au rechargement (le bouton ne faisait que vider
 * le store local). 404 = déjà supprimée (autre onglet, autre appareil) : succès. Autre échec : on
 * resynchronise depuis le serveur, la notification réapparaît donc au lieu de mentir.
 * Le serveur n'accepte que le destinataire (404 sinon) et émet `notification_deleted` aux autres
 * appareils.
 */
export const deleteNotificationOnServer = (notificationId) => async (dispatch) => {
  dispatch(clearNotification(notificationId))
  // Pas de test sur localStorage.userId : cette clé n'est pas posée par TOUS les chemins de connexion et n'est
  // qu'un état d'interface. L'authentification (cookie httpOnly) et le contrôle du destinataire sont faits par
  // le serveur ; sans session valide il répond 401 et la liste est resynchronisée.
  if (!isServerNotificationId(notificationId)) return
  try {
    await del(`/notifications/${notificationId}`)
  } catch (error) {
    if (error?.response?.status === 404) return
    console.error('Erreur lors de la suppression de la notification:', error)
    dispatch(fetchNotifications())
  }
}

export const markAllNotificationsReadOnServer = () => async (dispatch) => {
  // ✅ SECURITY: Vérifier via userId (cookie httpOnly gère l'auth)
  const userId = localStorage.getItem('userId')
  if (!userId) return

  // Optimiste : items lus + badge à 0 immédiatement, réseau en fond.
  dispatch(markAllNotificationsRead())
  try {
    await put('/notifications/read-all', {})
  } catch (error) {
    console.error('Erreur lors du marquage de toutes les notifications comme lues:', error)
    dispatch(fetchNotifications()) // rollback : resync depuis le serveur
  }
}

// Ouverture de la cloche (style LinkedIn) : on VIDE le badge à 0 instantanément
// côté front SANS toucher au flag `read` local des items → chaque ligne garde son
// surlignage "nouveau" pendant la session.
//
// ⚠️ Côté SERVEUR il n'existe pas d'état "vu" distinct : l'appel est bien
// `read-all` (persiste en READ). Donc "vu" est une nuance PUREMENT front (badge
// vidé, items encore surlignés localement) ; au prochain `fetchNotifications`,
// les items reviennent read=true du serveur et le surlignage disparaît alors.
export const clearUnreadBadgeOnOpen = () => async (dispatch) => {
  const userId = localStorage.getItem('userId')
  if (!userId) return

  dispatch(setUnreadCount(0)) // badge à 0 tout de suite (items locaux inchangés)
  try {
    await put('/notifications/read-all', {}) // persistance serveur = READ
  } catch (error) {
    console.error('Erreur lors du vidage du badge de notifications:', error)
    dispatch(fetchNotifications()) // échec : le badge remonte (resync)
  }
}

/**
 * Créer une notification pour un message dans un groupe de discussion
 * @param {Object} messageData - Données du message
 */
export const createDiscussionGroupMessageNotification = (messageData) => {
  const { groupId, groupTitle, messageId, messageContent, originalMessage, sender } = messageData

  const truncatedContent =
    messageContent && messageContent.length > 100
      ? messageContent.substring(0, 100) + '...'
      : messageContent || ''

  return {
    id: `discussion-group-message-${messageId}-${Date.now()}`,
    type: 'discussion_group_message',
    title: `${sender.firstName} ${sender.lastName}`,
    content: `a écrit un message dans le groupe "${groupTitle}": "${truncatedContent}"`,
    sender: {
      id: sender.id,
      firstName: sender.firstName,
      lastName: sender.lastName,
      avatarUrl: sender.avatarUrl,
    },
    groupId: groupId,
    groupTitle: groupTitle,
    messageId: messageId,
    messageContent: truncatedContent,
    originalMessage: originalMessage,
    timestamp: new Date().toISOString(),
    created_at: new Date().toISOString(),
    read: false,
  }
}

// Créer une notification pour une réaction sur un post
export const createPostReactionNotification = (reactionData) => {
  const { postId, reactionType, sender, postAuthor } = reactionData

  return {
    id: `post-reaction-${postId}-${sender.id}-${Date.now()}`,
    type: 'post_reaction',
    title: `${sender.firstName} ${sender.lastName}`,
    content: `a réagi ${getReactionEmoji(reactionType)} à votre publication`,
    sender: {
      id: sender.id,
      firstName: sender.firstName,
      lastName: sender.lastName,
      avatarUrl: sender.avatarUrl,
    },
    postId: postId,
    reactionType: reactionType,
    timestamp: new Date().toISOString(),
    created_at: new Date().toISOString(), // Ajout pour la compatibilité
    read: false,
  }
}

// Créer une notification pour un commentaire sur un post
export const createPostCommentNotification = (commentData) => {
  const { postId, commentId, comment, sender, postAuthor } = commentData

  const truncatedComment = comment.length > 50 ? comment.substring(0, 50) + '...' : comment

  return {
    id: `post-comment-${commentId}-${Date.now()}`,
    type: 'post_comment',
    title: `${sender.firstName} ${sender.lastName}`,
    content: `a commenté votre publication: "${truncatedComment}"`,
    sender: {
      id: sender.id,
      firstName: sender.firstName,
      lastName: sender.lastName,
      avatarUrl: sender.avatarUrl,
    },
    postId: postId,
    commentId: commentId,
    originalComment: comment,
    timestamp: new Date().toISOString(),
    created_at: new Date().toISOString(), // Ajout pour la compatibilité
    read: false,
  }
}

// Créer une notification pour être identifié sur une photo
export const createPostTagNotification = (tagData) => {
  const { postId, tagId, sender, taggedMember } = tagData

  return {
    id: `post-tag-${tagId}-${Date.now()}`,
    type: 'post_tag',
    title: `${sender.firstName} ${sender.lastName}`,
    content: 'vous a identifié sur une photo',
    sender: {
      id: sender.id,
      firstName: sender.firstName,
      lastName: sender.lastName,
      avatarUrl: sender.avatarUrl,
    },
    postId: postId,
    tagId: tagId,
    timestamp: new Date().toISOString(),
    created_at: new Date().toISOString(), // Ajout pour la compatibilité
    read: false,
  }
}

// Créer une notification pour être mentionné dans un post ou commentaire
export const createPostMentionNotification = (mentionData) => {
  const { postId, itemId, mentionType, sender, mentionedMember } = mentionData

  const content =
    mentionType === 'post'
      ? 'vous a mentionné dans une publication'
      : 'vous a mentionné dans un commentaire'

  return {
    id: `post-mention-${itemId}-${Date.now()}`,
    type: 'post_mention',
    title: `${sender.firstName} ${sender.lastName}`,
    content: content,
    sender: {
      id: sender.id,
      firstName: sender.firstName,
      lastName: sender.lastName,
      avatarUrl: sender.avatarUrl,
    },
    postId: postId,
    itemId: itemId,
    mentionType: mentionType,
    timestamp: new Date().toISOString(),
    created_at: new Date().toISOString(), // Ajout pour la compatibilité
    read: false,
  }
}

// Action pour supprimer une notification de réaction
export const removePostReactionNotification = (postId, senderId) => ({
  type: 'REMOVE_POST_REACTION_NOTIFICATION',
  payload: { postId, senderId },
})

/**
 * Créer une notification de mentorat (request / accepted / declined / completed)
 * @param {Object} data - { subType, mentorshipId, conversationId, topic, sender }
 */
export const createMentoringNotification = (data) => {
  const { subType, mentorshipId, conversationId, topic, sender = {} } = data
  const senderName = `${sender.firstName || ''} ${sender.lastName || ''}`.trim() || 'Un membre'

  const contentBySubType = {
    request: `vous a envoyé une demande de mentorat${topic ? ` (${topic})` : ''}`,
    accepted: 'a accepté votre demande de mentorat',
    declined: 'a décliné votre demande de mentorat',
    completed: 'a clôturé votre mentorat',
    feedback_request: 'Donnez votre avis sur ce mentorat',
    pending_reminder: 'Une demande de mentorat attend votre réponse depuis 7 jours',
    inactivity_notice: 'Ce mentorat est inactif depuis 90 jours — toujours en cours ?',
  }

  return {
    id: `mentoring-${subType}-${mentorshipId}-${Date.now()}`,
    type: `mentoring_${subType}`,
    title: senderName,
    content: contentBySubType[subType] || 'Notification mentorat',
    sender: {
      id: sender.id,
      firstName: sender.firstName,
      lastName: sender.lastName,
      avatarUrl: sender.avatarUrl,
    },
    mentorshipId,
    conversationId: conversationId || null,
    timestamp: new Date().toISOString(),
    created_at: new Date().toISOString(),
    read: false,
  }
}

// Fonction utilitaire pour obtenir l'emoji d'une réaction
const getReactionEmoji = (reactionType) => {
  const emojis = {
    like: '👍',
    celebrate: '🎉',
    support: '🙌',
    insightful: '💡',
  }
  return emojis[reactionType] || '👍'
}

// Action générique pour ajouter une notification de post
export const addPostNotification = (notificationData) => (dispatch) => {
  let notification

  switch (notificationData.type) {
    case 'profile_view':
      notification = createProfileViewNotification(notificationData)
      break
    case 'post_reaction':
      notification = createPostReactionNotification(notificationData)
      break
    case 'post_comment':
      notification = createPostCommentNotification(notificationData)
      break
    case 'post_tag':
      notification = createPostTagNotification(notificationData)
      break
    case 'post_mention':
      notification = createPostMentionNotification(notificationData)
      break
    case 'discussion_group_message':
      notification = createDiscussionGroupMessageNotification(notificationData)
      break
    case 'mentoring_request':
    case 'mentoring_accepted':
    case 'mentoring_declined':
    case 'mentoring_completed':
    case 'mentoring_feedback_request':
    case 'mentoring_pending_reminder':
    case 'mentoring_inactivity_notice':
      notification = createMentoringNotification({
        ...notificationData,
        subType: notificationData.type.replace('mentoring_', ''),
      })
      break
    default:
      console.warn('Type de notification non reconnu:', notificationData.type)
      return
  }

  dispatch(addNotification(notification))
}

// Créer une notification pour une vue de profil
export const createProfileViewNotification = (viewData) => {
  const { viewerId, viewerFirstName, viewerLastName, viewerAvatarUrl, timestamp } = viewData

  return {
    id: `profile-view-${viewerId}-${Date.now()}`,
    type: 'profile_view',
    title: `${viewerFirstName} ${viewerLastName}`,
    content: 'a consulté votre profil',
    sender: {
      id: viewerId,
      firstName: viewerFirstName,
      lastName: viewerLastName,
      avatarUrl: viewerAvatarUrl,
    },
    timestamp: timestamp || new Date().toISOString(),
    created_at: timestamp || new Date().toISOString(),
    read: false,
  }
}

// Fonction pour créer une notification lorsqu'un nouveau message est reçu
export const createMessageNotification = (message, conversation) => {
  if (!message || !conversation) {
    return null
  }

  // Normaliser les IDs en nombre
  const senderId = Number(message.sender_id)
  const conversationId = Number(message.conversation_id || conversation.id)

  // Trouver les informations de l'expéditeur
  const sender = message.sender || {}
  const otherParticipant = conversation.members?.find((m) => Number(m.id) === senderId) || {}

  // Construire la notification
  return {
    id: `msg-${message.id || Date.now()}`,
    type: 'message',
    title: otherParticipant.firstName
      ? `${otherParticipant.firstName} ${otherParticipant.lastName || ''}`
      : sender.firstName
        ? `${sender.firstName} ${sender.lastName || ''}`
        : 'Nouveau message',
    content: message.content || '',
    sender: {
      id: sender.id || senderId || 0,
      firstName: sender.firstName || otherParticipant.firstName || 'Utilisateur',
      lastName: sender.lastName || otherParticipant.lastName || '',
      avatarUrl: sender.avatarUrl || otherParticipant.avatarUrl || '',
    },
    conversationId: conversationId,
    timestamp: message.created_at || message.createdAt || new Date().toISOString(),
    created_at: message.created_at || message.createdAt || new Date().toISOString(), // Ajout pour la compatibilité
    read: false,
  }
}
